import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';

const AnnualCertificateManagement = ({ authAxios, users, showFormMessage, userRole }) => {
    // --- RBAC Permissions ---
    const isPartner = userRole === 'PARTNER' || userRole === 'SUPERUSER';

    const [certificates, setCertificates] = useState([]);
    const [editingCertificate, setEditingCertificate] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [filterType, setFilterType] = useState('');

    const staffUsers = users.filter(u => u.is_staff);
    const currentYear = new Date().getFullYear();

    const fetchCertificates = async () => {
        setIsLoading(true);
        try {
            const params = filterType ? { params: { certificate_type: filterType } } : {};
            const response = await authAxios.get('annual-certificates/', params);
            setCertificates(response.data.results || response.data);
        } catch (error) {
            console.error("Failed to fetch annual certificates:", error);
            showFormMessage('danger', 'Could not load certificates.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isPartner) {
            fetchCertificates();
        }
    }, [isPartner, authAxios]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const isEditing = editingCertificate && editingCertificate.id;
        const url = isEditing ? `annual-certificates/${editingCertificate.id}/` : 'annual-certificates/';
        const method = isEditing ? 'patch' : 'post';

        const formData = new FormData();
        Object.keys(editingCertificate).forEach(key => {
            if (key === 'certificate_file' && !(editingCertificate[key] instanceof File)) {
                return;
            }
             if (editingCertificate[key] !== null && editingCertificate[key] !== undefined) {
                formData.append(key, editingCertificate[key]);
            }
        });

        try {
            await authAxios[method](url, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            showFormMessage('success', `Certificate record ${isEditing ? 'updated' : 'created'} successfully!`);
            setEditingCertificate(null);
            fetchCertificates();
        } catch (error) {
            console.error("Failed to save certificate record:", error.response?.data);
            showFormMessage('danger', 'Failed to save certificate record.');
        }
    };

    const openNewCertificateForm = () => {
        setEditingCertificate({
            practitioner: '',
            year: currentYear,
            certificate_type: 'ANNUAL_PRACTICING',
            status: 'PENDING',
            submission_date: null,
            notes: '',
            certificate_file: null
        });
    };
    
    if (!isPartner) {
        return null;
    }

    return (
        <div className="p-4 animate__animated animate__fadeIn">
            <div className="flex justify-between items-center mb-4">
                <h4 className="text-xl font-bold text-gray-700">Practitioner Certificates</h4>
                <div className="flex items-center space-x-2">
                    <select
                        className="px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                        value={filterType}
                        onChange={e => { setFilterType(e.target.value); fetchCertificates(); }}
                    >
                        <option value=''>All Types</option>
                        <option value='ANNUAL_PRACTICING'>Annual Practicing</option>
                        <option value='ANNUAL_RETURN'>Annual Return</option>
                        <option value='ANNUAL_COMPLIANCE'>Annual Compliance</option>
                    </select>
                    <button 
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        onClick={openNewCertificateForm}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                        Add Certificate Record
                    </button>
                </div>
            </div>

            {isLoading ? (
                <p>Loading certificates...</p>
            ) : (
                <div className="overflow-x-auto bg-white rounded-lg shadow">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Practitioner</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submission Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {certificates.map(cert => (
                                <tr key={cert.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{cert.practitioner_name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cert.year}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cert.certificate_type_display || cert.certificate_type}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${cert.status === 'APPROVED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{cert.status}</span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cert.submission_date ? format(new Date(cert.submission_date), 'yyyy-MM-dd') : 'N/A'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {cert.certificate_file_url ? (
                                            <a href={cert.certificate_file_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-900">View File</a>
                                        ) : (
                                            'No File'
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <button className="text-indigo-600 hover:text-indigo-900" onClick={() => setEditingCertificate(cert)}>
                                            Edit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editingCertificate && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
                        <div className="flex justify-between items-center mb-4">
                            <h5 className="text-xl font-bold text-gray-800">{editingCertificate.id ? 'Edit' : 'Add'} Certificate Record</h5>
                            <button onClick={() => setEditingCertificate(null)} className="text-gray-600 hover:text-gray-900">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <form onSubmit={handleFormSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Practitioner</label>
                                <select
                                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    value={editingCertificate.practitioner}
                                    onChange={e => setEditingCertificate({...editingCertificate, practitioner: e.target.value})}
                                    required
                                >
                                    <option value="" disabled>Select a practitioner</option>
                                    {staffUsers.map(user => (
                                        <option key={user.id} value={user.id}>{user.username}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Year</label>
                                <input
                                    type="number"
                                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    value={editingCertificate.year}
                                    onChange={e => setEditingCertificate({...editingCertificate, year: e.target.value})}
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Certificate Type</label>
                                <select
                                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    value={editingCertificate.certificate_type}
                                    onChange={e => setEditingCertificate({...editingCertificate, certificate_type: e.target.value})}
                                    required
                                >
                                    <option value="ANNUAL_PRACTICING">Annual Practicing Certificate</option>
                                    <option value="ANNUAL_RETURN">Annual Return Filing</option>
                                    <option value="ANNUAL_COMPLIANCE">Annual Compliance Certificate</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Status</label>
                                <select
                                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    value={editingCertificate.status}
                                    onChange={e => setEditingCertificate({...editingCertificate, status: e.target.value})}
                                >
                                    <option value="PENDING">Pending Submission</option>
                                    <option value="SUBMITTED">Submitted for Review</option>
                                    <option value="APPROVED">Approved</option>
                                    <option value="REJECTED">Rejected</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Submission Date</label>
                                <input
                                    type="date"
                                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    value={editingCertificate.submission_date || ''}
                                    onChange={e => setEditingCertificate({...editingCertificate, submission_date: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Certificate File</label>
                                <input
                                    type="file"
                                    className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                    onChange={e => setEditingCertificate({...editingCertificate, certificate_file: e.target.files[0]})}
                                />
                            </div>
                            <div>
                                 <label className="block text-sm font-medium text-gray-700">Notes</label>
                                 <textarea
                                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    rows="3"
                                    value={editingCertificate.notes}
                                    onChange={e => setEditingCertificate({...editingCertificate, notes: e.target.value})}
                                 ></textarea>
                            </div>
                            <div className="flex justify-end space-x-2 pt-4">
                                <button type="button" className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300" onClick={() => setEditingCertificate(null)}>Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Save Record</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnnualCertificateManagement;
